import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { memo, useCallback, useContext, useEffect } from 'react';
import { MotionGraphicsWithIconTag } from '@/components/Icons/Sidebar/MotionGraphics';
import { useHasAnyRole } from '@/hooks/auth';
import { useSettings } from '@/hooks/state/useSettings';
import { logEvent } from '@/pages/api/v1/utils';
import { SidebarContext } from '@/providers/SidebarProvider';
import { useTracking } from '@/providers/TrackingProvider';
import { SidebarGroup, SidebarMenu, SidebarMenuItem, SidebarMenuButton, SidebarSeparator, SidebarGroupTitle } from '@/components/ui/sidebar';
import { DynamicLink } from '../DynamicLink';
import { MusicWithIconTag } from '../Icons/Sidebar/Music';
import { SoundEffectsWithIconTag } from '../Icons/Sidebar/SoundEffects';
import { LutsWithIconTag } from '../Icons/Sidebar/Luts';
import { StockVideoWithIconTag } from '../Icons/Sidebar/StockVideo';
import { TrendingWithIconTag } from '../Icons/Sidebar/Trending';
import { AIPGWithIconTag } from '../Icons/Sidebar/AIPG';
import { ReferWithIconTag } from '../Icons/Sidebar/Refer';
import { PricingWithIconTag } from '../Icons/Sidebar/Pricing';
import { HelpWithIconTag } from '../Icons/Sidebar/Help';
import { BlogWithIconTag } from '../Icons/Sidebar/Blog';
import { AccountWithIconTag } from '../Icons/Sidebar/Account';
import type { SidebarSections, Links } from './types';
import { sidebarLinkStyles } from './styles';
import { CircleSmall, Heart, Plus } from 'lucide-react';
import { Button } from '@music-vine/cadence/ui';
import { useBoardsContext } from '@/features/Boards/context-provider';
import Link from 'next/link';
import { DialogCreateBoard } from '@/features/Boards/dialog-create-board';
import type { GetBoardsItemResponse } from '@/open-api/model';
import { BoardDropTarget } from '@/features/Boards/sidebar-drag';
import { sidebarBoardDropEnabledAtom } from '@/features/Boards/sidebar-drag-atoms';
import { isPendingBoardId } from '@/features/Boards/utils/is-pending-board-id';
import { useSetAtom } from 'jotai';

// Dynamic import with SSR disabled to avoid hydration mismatches
const AppSidebarAdminLinks = dynamic(() => import('./AppSidebarAdminLinks/index').then(mod => mod.AppSidebarAdminLinks), {
  ssr: false,
  loading: () => null
});
function isLinkActive(link: Links, activePath: string, isSearchVisible: boolean): boolean {
  if (isSearchVisible) {
    return false;
  }
  return activePath === link.url || activePath === `/personalized${link.url}` || link.pathnames.some(path => activePath === path || activePath === `/personalized${path}`);
}
const BoardsGroupTitle = <>
    <Link className="grow" href="/my/boards">
      Boards
    </Link>
    <BoardDropTarget target="new">
      <span className="flex rounded-full transition data-drop-over:scale-110 data-drop-over:bg-pink-500/15 data-drag-active:text-pink-500 data-drop-over:ring-2 data-drop-over:ring-pink-500">
        <DialogCreateBoard mode="create_board_only">
          <Button aria-label="Create board" borderRadius="full" noFeedback size="icon" variant="transparent">
            <Plus className="size-4" />
          </Button>
        </DialogCreateBoard>
      </span>
    </BoardDropTarget>
  </>;
const getSidebarSections = (showAccountLinks: boolean, isPayingUser: boolean, showBoardLinks: boolean, {
  boards,
  favoritesBoardId
}: {
  boards: NonNullable<GetBoardsItemResponse>[];
  favoritesBoardId: number | null;
}): SidebarSections[] => {
  const boardLinks: Links[] = [{
    url: '/my/boards/favorites',
    label: 'Favorites',
    icon: <Heart />,
    pathnames: ['/my/boards/favorites'],
    prefetch: true,
    boardId: favoritesBoardId ?? undefined
  }, ...boards.map(board => ({
    url: `/my/boards/${board.id}`,
    label: board.name,
    useAsPath: true,
    icon: <CircleSmall />,
    pathnames: [`/my/boards/${board.id}`],
    prefetch: false,
    boardId: isPendingBoardId(board.id) ? undefined : board.id
  }))];
  return [{
    name: 'Music Links',
    links: [{
      url: '/music',
      label: 'Music',
      icon: <MusicWithIconTag className="[&_path]:size-5!" />,
      pathnames: ['/music/collection/[...collectionSlug]', '/music/category/[...categorySlug]', '/music/similar/[artistId]/[trackId]', '/music', '/music/tracks/[artistSlug]/[trackSlug]', '/music/zones/[zoneSlug]'],
      prefetch: true
    }, {
      url: '/sfx',
      label: 'Sound effects',
      icon: <SoundEffectsWithIconTag />,
      pathnames: ['/sfx/[sfxSlug]/[sfxId]/[variantId]', '/sfx/category/[...categorySlug]', '/sfx/collection/[...collectionSlug]'],
      prefetch: true
    }, {
      url: '/stock-video',
      label: 'Stock video',
      icon: <StockVideoWithIconTag />,
      pathnames: ['/stock-video', '/stock-video/asset/[svSlug]', '/stock-video/category/[...categorySlug]', '/stock-video/explore'],
      prefetch: true
    }, {
      url: '/motion-graphics',
      label: 'Motion graphics',
      icon: <MotionGraphicsWithIconTag />,
      pathnames: ['/motion-graphics', '/motion-graphics/asset/[mgSlug]', '/motion-graphics/[...mgCategoryPath]'],
      prefetch: true
    }, {
      url: '/luts',
      label: 'LUTs',
      icon: <LutsWithIconTag />,
      pathnames: ['/luts', '/luts/asset/[lutSlug]', '/luts/[...lutCategoryPath]'],
      prefetch: true
    }, {
      url: '/trending',
      label: 'Trending',
      icon: <TrendingWithIconTag />,
      pathnames: ['/trending'],
      prefetch: true
    }, {
      url: '/playlist-generator',
      label: 'Playlist generator',
      icon: <AIPGWithIconTag />,
      pathnames: ['/playlist-generator/[[...uuid]]'],
      prefetch: true
    }]
  }, {
    name: 'Pricing Links',
    visible: !isPayingUser,
    links: isPayingUser ? [] : [{
      url: '/pricing',
      label: 'Pricing',
      icon: <PricingWithIconTag />,
      pathnames: [],
      prefetch: true
    }]
  }, {
    name: 'Account Links',
    visible: showAccountLinks,
    links: [{
      url: '/artist/downloads',
      label: 'Account',
      icon: <AccountWithIconTag />,
      pathnames: ['/artist/downloads']
    }]
  }, {
    name: 'Misc Links',
    links: [{
      url: '/refer-and-earn',
      label: 'Refer & earn',
      icon: <ReferWithIconTag />,
      pathnames: []
    }, {
      url: '/help-center',
      label: 'Help center',
      icon: <HelpWithIconTag />,
      pathnames: ['/help-center/[slug]']
    }, {
      url: '/blog/',
      label: 'Blog',
      icon: <BlogWithIconTag />,
      pathnames: []
    }]
  }, {
    name: 'Boards',
    visible: showBoardLinks,
    groupTitle: BoardsGroupTitle,
    links: boardLinks
  }];
};
const AppSidebarLinks = memo(() => {
  const router = useRouter();
  const activePath = router.pathname;
  const activeAsPath = router.asPath;
  const {
    settings
  } = useSettings();
  const maximumBoardsToShow = 5;
  const isLoggedIn = !!settings?.user?.is_authenticated;
  const isArtist = !!settings?.user?.hasAccountArtist;
  const isPayingUser = useHasAnyRole(['Essentials', 'Creator', 'Pro', 'Business']);
  const {
    toggleSidebar,
    toggleSearch,
    isSearchVisible,
    isSidebarToggled
  } = useContext(SidebarContext);
  const {
    boards = [],
    favoritesBoardId = null
  } = useBoardsContext() ?? {};
  const boardsNoFavorite = boards.filter((board): board is NonNullable<GetBoardsItemResponse> => !!board && board.name !== 'Favorites').slice(0, maximumBoardsToShow);
  const showBoardLinks = isLoggedIn && !isSidebarToggled;
  const sidebarSections = getSidebarSections(isArtist, isPayingUser, showBoardLinks, {
    boards: boardsNoFavorite,
    favoritesBoardId
  });
  const setBoardDropEnabled = useSetAtom(sidebarBoardDropEnabledAtom);
  useEffect(() => {
    setBoardDropEnabled(showBoardLinks);
    return () => setBoardDropEnabled(false);
  }, [showBoardLinks, setBoardDropEnabled]);
  const visibleSections = sidebarSections.filter(section => section.visible !== false);
  const {
    initializeTracking
  } = useTracking();
  const handleLinkClick = useCallback((label: string, url: string) => {
    void logEvent({
      event_type: 'click',
      asPath: router.asPath,
      metadata: {
        type: 'sidebar_link',
        text: label,
        next_path: url
      }
    });
    void initializeTracking({
      content: label,
      endSlug: url,
      access: 'Sidebar'
    });
    toggleSidebar(true, 'mobile');
    toggleSearch(false);
  }, [initializeTracking, toggleSidebar, toggleSearch, router.asPath]);
  return <>
      {visibleSections.map((section, sectionIndex) => {
      const visibleLinks = section.links.filter(link => link.visible !== false);
      if (visibleLinks.length === 0) {
        return null;
      }
      return <SidebarGroup className="gap-1.5 px-3 pt-4 pb-0 first:pt-[34px]" key={section.name}>
            {!!section.groupTitle && <SidebarGroupTitle className="pr-0 pl-3 text-gray-800 text-sm uppercase dark:text-white">
                {section.groupTitle}
              </SidebarGroupTitle>}
            <SidebarMenu className="gap-[0.375rem]">
              {visibleLinks.map(link => {
            const active = isLinkActive(link, link.useAsPath ? activeAsPath : activePath, isSearchVisible);
            const item = <SidebarMenuItem className="rounded-lg transition data-drop-over:bg-pink-500/10 data-drop-over:ring-2 data-drop-over:ring-pink-500" key={`sidebar-link-${link.url}`}>
                    <SidebarMenuButton asChild className={sidebarLinkStyles} isActive={active} tooltip={isSidebarToggled ? link.label : undefined}>
                      <DynamicLink href={link.url} onClick={() => handleLinkClick(link.label, link.url)} prefetch={link.prefetch}>
                        {link.icon}
                        <span className="flex-1 group-data-[collapsible=icon]:hidden">
                          {link.label}
                        </span>
                        {!!link.suffix && <span className="group-data-[collapsible=icon]:hidden">
                            {link.suffix}
                          </span>}
                      </DynamicLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>;
            return link.boardId === undefined ? item : <BoardDropTarget key={`sidebar-link-${link.url}`} target={link.boardId}>
                    {item}
                  </BoardDropTarget>;
          })}
            </SidebarMenu>
            {sectionIndex < visibleSections.length - 1 && <SidebarSeparator className="mx-0 mt-2.5 bg-gray-150 dark:bg-gray-800" />}
          </SidebarGroup>;
    })}
      <AppSidebarAdminLinks />
    </>;
});
AppSidebarLinks.displayName = 'AppSidebarLinks';
export default AppSidebarLinks;